export const CHAT_PAYLOAD_KINDS = {
  LOCATION: "chat_location",
  POLL: "chat_poll",
};

export function normalizeChatPayloadKind(payload) {
  return String(payload?.kind || "").trim().toLowerCase();
}

export function isChatPollPayload(payload) {
  const kind = normalizeChatPayloadKind(payload);
  return kind === CHAT_PAYLOAD_KINDS.POLL || kind === "concept_date_poll";
}

export function getChatLocationPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (normalizeChatPayloadKind(payload) !== CHAT_PAYLOAD_KINDS.LOCATION) return null;

  const lat = Number(payload.lat ?? payload.latitude);
  const lng = Number(payload.lng ?? payload.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return {
    ...payload,
    lat,
    lng,
    address: String(payload.address || payload.label || "").trim(),
    caption: String(payload.caption || "").trim(),
  };
}

export function getChatPollPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (normalizeChatPayloadKind(payload) !== CHAT_PAYLOAD_KINDS.POLL) return null;

  const title = String(payload.title || "").trim();
  const options = Array.isArray(payload.options)
    ? payload.options
        .map((option, index) => {
          const id = String(option?.id || `opt_${index + 1}`).trim();
          const text = String(option?.text || option?.label || "").trim();
          const voterIds = Array.isArray(option?.voter_ids)
            ? option.voter_ids
                .map((voterId) => Number(voterId))
                .filter((voterId) => Number.isFinite(voterId) && voterId > 0)
            : [];
          const voters = Array.isArray(option?.voters)
            ? option.voters
                .map((voter) => {
                  const userId = Number(voter?.user_id || voter?.id || 0);
                  if (!Number.isFinite(userId) || userId <= 0) return null;
                  return {
                    ...voter,
                    user_id: userId,
                    avatar: String(voter?.avatar || "").trim(),
                    avatar_custom_url: String(voter?.avatar_custom_url || "").trim(),
                    avatar_url: String(voter?.avatar_url || "").trim(),
                  };
                })
                .filter(Boolean)
            : [];
          if (!id || !text) return null;
          return {
            ...option,
            id,
            text,
            voter_ids: voterIds,
            voters,
          };
        })
        .filter(Boolean)
    : [];

  if (!title || options.length < 2) return null;
  return {
    ...payload,
    title,
    options,
    caption: String(payload.caption || "").trim(),
    allow_multiple: Boolean(payload.allow_multiple),
  };
}

export function formatChatLocationDisplayAddress(address, fallback = "") {
  const raw = String(address || fallback || "").replace(/\s+/g, " ").trim();
  if (!raw) return "";
  if (/^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(raw)) return raw;

  const countryNames = new Set([
    "slovakia",
    "slovak republic",
    "slovensko",
    "slovenska republika",
  ]);

  const cleanedParts = raw
    .split(",")
    .map((part) =>
      String(part || "")
        .replace(/\b\d{3}\s?\d{2}\b/g, "")
        .replace(/\b(Bratislava)\s+(?:I|II|III|IV|V|1|2|3|4|5)\b/gi, "$1")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter((part) => part && !countryNames.has(part.toLowerCase()));

  const deduped = [];
  cleanedParts.forEach((part) => {
    const normalized = part.toLowerCase();
    if (!deduped.some((existing) => existing.toLowerCase() === normalized)) {
      deduped.push(part);
    }
  });

  return deduped.join(", ") || raw;
}

export function buildChatLocationMarkers(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const location = getChatLocationPayload(item?.payload);
      if (!location) return null;

      const username = String(item?.sender_username || item?.author_username || "").trim();
      const code = String(item?.sender_code || item?.author_code || "").trim().toUpperCase();
      const authorLabel = username
        ? (username.startsWith("@") ? username : `@${username}`)
        : code || "User";

      return {
        id: item?.id,
        messageId: item?.id,
        lat: location.lat,
        lng: location.lng,
        address: formatChatLocationDisplayAddress(
          location.address,
          `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`
        ),
        caption: location.caption,
        authorLabel,
        createdAt: item?.created_at || null,
        time: item?.time || "",
      };
    })
    .filter(Boolean);
}
